<template>
  <div v-if="loaded" class="container py-5 pt-3 pt-xl-5">
    <button
      class="btn btn-outline-secondary mb-4"
      @click="goBackToRoleSelection"
    >
      <font-awesome-icon icon="arrow-left" class="me-2" />
      {{ $t("common.back") }}
    </button>

    <!-- Keyed on the country too, so a country switch starts that country's flow afresh. -->
    <EESignupFlow v-if="activeFlow === 'EE'" :key="flowKey" />

    <LVSignupFlow v-if="activeFlow === 'LV'" :key="flowKey" />

    <SESignupFlow v-if="activeFlow === 'SE'" :key="flowKey" />

    <LTSignupFlow v-if="activeFlow === 'LT'" :key="flowKey" />

    <FISignupFlow v-if="activeFlow === 'FI'" :key="flowKey" />

    <!-- Every other country: create the company, then book a demo -->
    <InternationalSignupFlow
      v-if="activeFlow === 'international'"
      :key="flowKey"
    />

    <EmptySignupFlow v-if="activeFlow === 'empty'" />
  </div>
</template>

<script setup lang="ts">
import { useAccount } from "@/composables/account/useAccount";
import { useRouteCompany } from "@/composables/route/useCompany";
import { OWN_SIGNUP_FLOW_COUNTRIES } from "@/constants";
import EESignupFlow from "@/signup/ee/EESignupFlow.vue";
import EmptySignupFlow from "@/signup/EmptySignupFlowComponent.vue";
import FISignupFlow from "@/signup/fi/FISignupFlow.vue";
import InternationalSignupFlow from "@/signup/international/InternationalSignupFlow.vue";
import LTSignupFlow from "@/signup/lt/LTSignupFlow.vue";
import LVSignupFlow from "@/signup/lv/LVSignupFlow.vue";
import SESignupFlow from "@/signup/se/SESignupFlow.vue";
import {
  getCountryFromCompanyOrLanguage,
  useSignupStore,
} from "@/signup/signup";
import { signupApi } from "@/signup/signupApi";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const { country: selectedCountry } = storeToRefs(signupStore);

type FlowKey = "empty" | "EE" | "FI" | "LV" | "SE" | "LT" | "international";

const companyId = useRouteCompany();

// Single source of truth for flow selection. The flows are mutually exclusive, so the
// decision lives here rather than being smeared across overlapping per-block v-ifs.
//
// Estonia, Latvia, Lithuania, Sweden and Finland have their own registry flows. Every other
// country gets the international flow, which creates the company and sends the producer to book a
// demo. Admins onboard the same way as producers signing up themselves.
const activeFlow = computed<FlowKey>(() => {
  const country = selectedCountry.value;
  if (!country) return "empty";
  return OWN_SIGNUP_FLOW_COUNTRIES.includes(country)
    ? (country as FlowKey)
    : "international";
});

const flowKey = computed(() => `${activeFlow.value}-${selectedCountry.value}`);

// The flows wait for this page's account: the signup store outlives the page, so on re-entry it
// still holds the previous visit's draft, which the flows would otherwise edit on mount.
const { loaded } = useAccount((fetchedAccountData) => {
  signupStore.initialize({
    initialAccount: fetchedAccountData,
    // Necessary so we can click "continue registration"
    defaultCountry: getCountryFromCompanyOrLanguage(
      fetchedAccountData,
      companyId.value
    ),
  });
});

const goBackToRoleSelection = () => {
  router.push("/signup");
};

onMounted(() => {
  signupApi
    .setOnboardingStep("PRODUCER")
    .catch((error) => globalErrorHandler(error));
});
</script>
